import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QBox, QButton, QLoader, QStack, QText, QTextField } from '@captivateiq/quotient';

import { useAIShellContext } from '@captivateiq/assistant/AIShellContext';
import type { ConversationListItem } from '@captivateiq/assistant/types';
import { HistoryEntry } from './HistoryEntry';
import { TabLayout } from '../TabLayout';
import { expandedTextColumnSx } from '../shared/expandedTextColumnSx';

export const HistoryTab = () => {
  const { t } = useTranslation('assistant');
  const { getConversations, isExpanded } = useAIShellContext();

  const [conversations, setConversations] = useState<Array<ConversationListItem>>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setHasError(false);
      try {
        const result = await getConversations();
        setConversations(result?.data || []);
        setTotalCount(result?.total_count ?? 0);
      } catch (error) {
        console.error('HistoryTab: Error fetching conversations:', error);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [getConversations, setConversations]);

  const hasMore = (conversations?.length || 0) < totalCount;

  if (isLoading) {
    return (
      <QBox
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <QLoader />
      </QBox>
    );
  }

  if (hasError) {
    return (
      <QStack
        direction="column"
        sx={{
          alignItems: 'stretch',
          height: '100%',
        }}
      >
        <QText sx={{ textAlign: 'center', mt: 4 }}>{t('messages.error_loading_chats')}</QText>
      </QStack>
    );
  }

  return totalCount ? (
    <TabLayout
      bottomContent={
        hasMore && (
          <QBox sx={expandedTextColumnSx(isExpanded)}>
            <QButton
              sx={{
                borderRadius: '4px',
                margin: 0,
                width: '100%',
              }}
              variant="outlined"
            >
              {t('button_labels.load_more')}
            </QButton>
          </QBox>
        )
      }
      contentContainerProps={{
        sx: {
          padding: '4px',
          gap: '4px',
          backgroundColor: 'white',
          ...expandedTextColumnSx(isExpanded),
        },
      }}
      topContent={
        <QBox sx={expandedTextColumnSx(isExpanded)}>
          <QTextField optional placeholder={t('input_placeholders.search') as string} />
        </QBox>
      }
    >
      {conversations.map((conversation) => (
        <HistoryEntry
          id={conversation.id}
          key={conversation.id}
          lastUpdated={conversation.updated_at}
          title={conversation.name}
        />
      ))}
    </TabLayout>
  ) : (
    <QStack
      direction="column"
      sx={{
        alignItems: 'stretch',
        height: '100%',
      }}
    >
      <QText sx={{ textAlign: 'center', mt: 4 }}>{t('messages.no_chats')}</QText>
    </QStack>
  );
};
